// Global Instructions Rule Applied!

import React, { forwardRef } from 'react'
import { InputSize, SelectTypeEnum, FieldValue } from '../types.ts'
import Select from '../../Select.jsx'
import { useLookups } from '../../../../app_modules/global_configs/hooks/useLookups.ts'
import { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'

export interface SelectInputProps {
  value: FieldValue
  onChange: (value: string | number | boolean | null) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  size?: InputSize
  status?: 'error' | 'warning'
  placeholder?: string
  options?: SelectListEntry[]
  selectType?: SelectTypeEnum
  lookupCategory?: string
  showSearch?: boolean
  allowClear?: boolean
  disabled?: boolean
  autoFocus?: boolean
  mode?: 'single' | 'multiple'
  optionsFooter?: React.ReactNode
  optionOrder?: 'label' | 'given'
}

export const SelectInput = forwardRef<any, SelectInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      size = 'small',
      status,
      placeholder,
      options = [],
      selectType,
      lookupCategory,
      showSearch,
      allowClear,
      disabled,
      autoFocus,
      mode = 'single',
      optionsFooter,
      optionOrder = 'label'
    },
    ref
  ) => {
    const { getLookupOptions } = useLookups()

    // Copy before sorting: sort() mutates, and the options array is often a
    // reference into Redux state (e.g. cfg options_json from globalConfigs).
    // Sorting it in place trips the store's immutability guard, which then
    // throws on every later dispatch and wedges the screen.
    // A field with an order of its own (optionOrder 'given') is shown as
    // handed in.
    const byLabel = (list: any[]): any[] =>
      optionOrder === 'given' ? [...list] : [...list].sort((a, b) => a.label.localeCompare(b.label))
    const selectOptions: any[] =
      selectType && selectType === SelectTypeEnum.LOOKUP && lookupCategory
        ? byLabel(getLookupOptions(lookupCategory))
        : byLabel(options)

    // Options that explain themselves. A lookup whose vocabulary the user is not
    // expected to know (Critical Technology Area) carries a summary line per
    // option; every other option source leaves shortDescription undefined and
    // renders exactly as before, so this costs nothing where it isn't used.
    // AntD's own listHeight keeps the popup scrollable at any list length.
    const described = selectOptions.some((option) => option?.shortDescription)

    // Search has to match the summary too: someone looking for
    // "supercomputing" will not type "Advanced Computing and Software".
    // optionFilterProp='label' can't see the extra key, so described lists
    // supply their own matcher.
    const matchOption = (input: string, option: any): boolean => {
      const needle = (input || '').toLowerCase()
      if (!needle) return true
      return (
        String(option?.label ?? '').toLowerCase().includes(needle) ||
        String(option?.shortDescription ?? '').toLowerCase().includes(needle)
      )
    }

    const renderOption = (option: any) => (
      <div className='py-0.5'>
        <div className='leading-snug'>{option?.label}</div>
        {option?.shortDescription ? (
          <div className='text-xs text-black/[0.45] leading-snug whitespace-normal'>{option.shortDescription}</div>
        ) : null}
      </div>
    )

    // A clear (the x icon, or Backspace on the selection) reaches onChange
    // as `undefined`. Passed back as the field's value that makes the AntD
    // Select UNCONTROLLED (rc-util's useMergedState treats only undefined
    // as "no controlled value"), so its last internal value - the option
    // just cleared - shows again and the confirm sees no change: the fund
    // Terms tab's Frequency could not be cleared. A clear is the explicit
    // value null, which the date / number / time inputs already emit and
    // the PATCH path already sends as "blank this column".
    const handleChange = (next: string | number | boolean | null | undefined) => {
      onChange(next === undefined ? null : next)
    }

    return (
      <div onKeyDown={onKeyDown}>
        <Select
          ref={ref}
          value={value}
          onChange={handleChange}
          options={selectOptions}
          size={size}
          status={status}
          placeholder={placeholder}
          showSearch={showSearch}
          filterOption={described ? matchOption : true}
          optionFilterProp={described ? undefined : 'label'}
          optionRender={described ? (item: any) => renderOption(item?.data ?? item) : undefined}
          optionLabelProp={described ? 'label' : undefined}
          allowClear={allowClear}
          disabled={disabled}
          style={{ width: '100%' }}
          autoFocus={autoFocus}
          popupRender={
            optionsFooter
              ? (menu: React.ReactNode) => (
                  <>
                    {menu}
                    {optionsFooter}
                  </>
                )
              : undefined
          }
          mode={mode === 'multiple' ? 'multiple' : undefined}
        />
      </div>
    )
  }
)

SelectInput.displayName = 'SelectInput'
